
import { useEffect, useState } from 'react';
import type { Track, Playlist } from '../page';
import SafeImage from './SafeImage';
import { addPlaylistCollaborator, getPlaylistCollaborators, searchUsers } from '../../../api/client';
import { useAuth } from '../../../context/AuthContext';

interface PlaylistManagerProps {
  playlists: Playlist[];
  selectedPlaylist: number;
  setSelectedPlaylist: (id: number) => void;
  onAddPlaylist: (name: string, description: string, visibility: 'public' | 'followers' | 'private') => void;
  onDeletePlaylist: (id: number) => void;
  onAddTrack: (playlistId: number, track: Track) => void;
  availableTracks: Track[];
}

export default function PlaylistManager({ 
  playlists, 
  selectedPlaylist, 
  setSelectedPlaylist, 
  onAddPlaylist, 
  onDeletePlaylist,
  onAddTrack,
  availableTracks 
}: PlaylistManagerProps) {
  const { isPro } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAddTrack, setShowAddTrack] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [newPlaylistDescription, setNewPlaylistDescription] = useState('');
  const [newPlaylistVisibility, setNewPlaylistVisibility] = useState<'public' | 'followers' | 'private'>('public');
  const [collabQuery, setCollabQuery] = useState('');
  const [collabResults, setCollabResults] = useState<Array<{ id: number; username: string; display_name: string }>>([]);
  const [collaborators, setCollaborators] = useState<Array<{ user_id: number; username: string; display_name: string }>>([]);

  const handleCreatePlaylist = () => {
    if (newPlaylistName.trim()) {
      onAddPlaylist(newPlaylistName.trim(), newPlaylistDescription.trim(), newPlaylistVisibility);
      setNewPlaylistName('');
      setNewPlaylistDescription('');
      setNewPlaylistVisibility('public');
      setShowCreateForm(false);
    }
  };

  const handleAddTrack = (track: Track) => {
    onAddTrack(selectedPlaylist, track);
    setShowAddTrack(false);
  };

  const currentPlaylist = playlists.find(p => p.id === selectedPlaylist);
  const availableTracksToAdd = availableTracks.filter(track => 
    !currentPlaylist?.tracks.some(t => t.id === track.id)
  );

  const loadCollaborators = async (playlistId: number) => {
    try {
      const list = await getPlaylistCollaborators(playlistId);
      setCollaborators(list);
    } catch {
      setCollaborators([]);
    }
  };

  useEffect(() => {
    if (selectedPlaylist) {
      loadCollaborators(selectedPlaylist);
    }
  }, [selectedPlaylist]);

  useEffect(() => {
    if (!isPro) {
      setCollabQuery('');
      setCollabResults([]);
      if (newPlaylistVisibility !== 'public') {
        setNewPlaylistVisibility('public');
      }
    }
  }, [isPro, newPlaylistVisibility]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-800">Manage Playlists</h2>
          <button
            onClick={() => setShowCreateForm(true)}
            className="w-8 h-8 flex items-center justify-center bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-colors"
          >
            <i className="ri-add-line text-sm"></i>
          </button>
        </div>
        
        <p className="text-gray-600 text-sm">Create and organize your music collections</p>
      </div>

      {/* Create Playlist Form */}
      {showCreateForm && (
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800">Create New Playlist</h3>
            <button
              onClick={() => setShowCreateForm(false)}
              className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
          
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Playlist name"
              value={newPlaylistName}
              onChange={(e) => setNewPlaylistName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-orange-500"
            />
            <input
              type="text"
              placeholder="Description (optional)"
              value={newPlaylistDescription}
              onChange={(e) => setNewPlaylistDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-orange-500"
            />
            <select
              value={newPlaylistVisibility}
              onChange={(e) => setNewPlaylistVisibility(e.target.value as 'public' | 'followers' | 'private')}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-orange-500"
            >
              <option value="public">Public</option>
              <option value="followers" disabled={!isPro}>
                {isPro ? 'Followers only' : 'Followers only (Pro)'}
              </option>
              <option value="private" disabled={!isPro}>
                {isPro ? 'Private' : 'Private (Pro)'}
              </option>
            </select>
            {!isPro && (
              <p className="text-xs text-amber-800">
                Private and followers-only playlists require Pro.{' '}
                <a href="/pricing" className="font-medium underline underline-offset-2">
                  Upgrade
                </a>
                .
              </p>
            )}
            <div className="flex gap-2">
              <button
                onClick={handleCreatePlaylist}
                disabled={!newPlaylistName.trim()}
                className="flex-1 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Playlist
              </button>
              <button
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Playlist List */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-sm">
        <h3 className="font-semibold text-gray-800 mb-4">Your Playlists</h3>
        {playlists.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-full mx-auto mb-4">
              <i className="ri-play-list-line text-stone-400 text-2xl"></i>
            </div>
            <p className="text-gray-600 text-sm font-medium">No playlists yet</p>
            <p className="text-gray-500 text-xs">Create one using the button above to get started</p>
          </div>
        ) : (
        <div className="space-y-3">
          {playlists.map((playlist) => (
            <div 
              key={playlist.id}
              className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                selectedPlaylist === playlist.id 
                  ? 'bg-orange-50 border border-orange-200' 
                  : 'hover:bg-gray-50'
              }`}
              onClick={() => setSelectedPlaylist(playlist.id)}
              onMouseUp={() => loadCollaborators(playlist.id)}
            >
              <SafeImage src={playlist.coverImage} alt={playlist.name} size="sm" />
              <div className="flex-1 min-w-0">
                <h4 className={`font-medium text-sm truncate ${
                  selectedPlaylist === playlist.id ? 'text-orange-600' : 'text-gray-800'
                }`}>
                  {playlist.name}
                </h4>
                <p className="text-xs text-gray-600 truncate">{playlist.description}</p>
                <p className="text-xs text-gray-500">{playlist.tracks.length} songs</p>
                <p className="text-[11px] text-gray-400 capitalize">{playlist.visibility || 'public'}</p>
              </div>
              <div className="flex items-center gap-1">
                {selectedPlaylist === playlist.id && (
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-check-line text-orange-600 text-xs"></i>
                  </div>
                )}
                {playlists.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeletePlaylist(playlist.id);
                    }}
                    className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <i className="ri-delete-bin-line text-xs"></i>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        )}
      </div>

      {/* Selected Playlist Details */}
      {currentPlaylist && (
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800">{currentPlaylist.name}</h3>
            <button
              onClick={() => setShowAddTrack(true)}
              className="px-3 py-1 bg-orange-100 text-orange-600 rounded-lg text-xs font-medium hover:bg-orange-200 transition-colors"
            >
              Add Songs
            </button>
          </div>
          
          <div className="flex items-center gap-3 mb-4">
            <SafeImage src={currentPlaylist.coverImage} alt={currentPlaylist.name} size="md" />
            <div>
              <p className="text-gray-600 text-sm">{currentPlaylist.description}</p>
              <p className="text-gray-500 text-xs">Created: {currentPlaylist.createdAt}</p>
              <p className="text-gray-500 text-xs">{currentPlaylist.tracks.length} songs</p>
            </div>
          </div>

          <div className="mb-4 p-3 rounded-lg bg-stone-50">
            <p className="text-xs font-medium text-gray-700 mb-2">Collaborators</p>
            {!isPro ? (
              <div className="mb-2 rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-xs text-amber-900">
                Collaborators are a Pro feature.{' '}
                <a href="/pricing" className="font-medium underline underline-offset-2">
                  Upgrade
                </a>
                .
              </div>
            ) : (
              <>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={collabQuery}
                    onChange={(e) => setCollabQuery(e.target.value)}
                    placeholder="Search users to invite"
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-xs"
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      const rows = await searchUsers({ search: collabQuery, filter: 'all' });
                      setCollabResults(rows.slice(0, 5));
                    }}
                    className="px-3 py-2 bg-stone-200 text-stone-700 rounded-lg text-xs"
                  >
                    Search
                  </button>
                </div>
                {collabResults.length > 0 && (
                  <div className="space-y-1 mb-2">
                    {collabResults.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={async () => {
                          await addPlaylistCollaborator(currentPlaylist.id, u.id);
                          await loadCollaborators(currentPlaylist.id);
                          setCollabResults([]);
                          setCollabQuery('');
                        }}
                        className="w-full text-left px-2 py-1 rounded bg-white hover:bg-orange-50 text-xs"
                      >
                        Invite {u.display_name} (@{u.username})
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
            {collaborators.length > 0 && (
              <p className="text-xs text-gray-500">
                {collaborators.map((c) => c.display_name || c.username).join(', ')}
              </p>
            )}
          </div>

          {currentPlaylist.tracks.length === 0 ? (
            <div className="text-center py-6">
              <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
                <i className="ri-music-line text-gray-400 text-lg"></i>
              </div>
              <p className="text-gray-500 text-sm">No songs in this playlist</p>
              <p className="text-gray-400 text-xs">Tap "Add Songs" to get started</p>
            </div>
          ) : (
            <div className="space-y-2">
              {currentPlaylist.tracks.slice(0, 3).map((track) => (
                <div key={track.id} className="flex items-center gap-3 p-2 rounded-lg bg-gray-50">
                  <SafeImage src={track.image} alt={track.title} size="xs" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-800 truncate">{track.title}</p>
                    <p className="text-xs text-gray-600 truncate">{track.artist}</p>
                  </div>
                </div>
              ))}
              {currentPlaylist.tracks.length > 3 && (
                <p className="text-xs text-gray-500 text-center py-2">
                  +{currentPlaylist.tracks.length - 3} more songs
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add Track Modal */}
      {showAddTrack && (
        <div className="fixed inset-0 bg-black/50 flex items-end z-50">
          <div className="bg-white rounded-t-2xl w-full max-h-[70vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-800">Add Songs to {currentPlaylist?.name}</h3>
                <button
                  onClick={() => setShowAddTrack(false)}
                  className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[calc(70vh-80px)]">
              {availableTracksToAdd.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                    <i className="ri-music-line text-gray-400 text-2xl"></i>
                  </div>
                  <p className="text-gray-500 text-sm">All available songs are already in this playlist</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {availableTracksToAdd.map((track) => (
                    <div 
                      key={track.id}
                      onClick={() => handleAddTrack(track)}
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <SafeImage src={track.image} alt={track.title} size="sm" />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm text-gray-800 truncate">{track.title}</h4>
                        <p className="text-xs text-gray-600 truncate">{track.artist}</p>
                      </div>
                      <div className="w-6 h-6 flex items-center justify-center text-orange-500">
                        <i className="ri-add-line text-sm"></i>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
